import { PropsWithChildren, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "../hooks/useTranslations";
import { getMetropolisUrl, getWebdeskUrl } from "../http/serviceportalApi";
import { App, Department, Permission } from "../types";
import { flattenPermissions, includesAny } from "../utils";
import { useMember } from "./memberContext";
import NotificationDispatch, {
  showErrorNotification,
} from "./notificationContext";

type AppsContextType = {
  apps: App[];
  recentlyUsedApps: App[];
  isValidating: boolean;
};

const AppsContext = createContext<AppsContextType>(undefined!);

const LOCAL_STORAGE_KEY_APPS = "recently-used-apps";

const openInNewTab = (url: string) => {
  window.open(url, "_blank");
};

const getRecentlyUsedAppIds = (): string[] => {
  const ids = localStorage.getItem(LOCAL_STORAGE_KEY_APPS);
  return ids ? (JSON.parse(ids) as string[]) : [];
};

const addRecentlyUsedApp = (app: App) => {
  const appIds = getRecentlyUsedAppIds();
  localStorage.setItem(
    LOCAL_STORAGE_KEY_APPS,
    JSON.stringify([app.id, ...appIds.filter((id) => id !== app.id)]),
  );
};

export const AppsProvider = ({ children }: PropsWithChildren) => {
  const { i18n } = useTranslations();
  const { member, status } = useMember();
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  const userMemberPermissions = member ? flattenPermissions(member) : [];

  const checkHasAnyPermission = (permissions: Permission[]) =>
    includesAny(userMemberPermissions, permissions);

  const accounting: Department = "accounting";
  const joinHospitality: Department = "joinHospitality";
  const payroll: Department = "payroll";
  const webMarketing: Department = "webMarketing";

  const handleError = (error: unknown) =>
    dispatch(showErrorNotification(error, i18n.appOpenError));

  const apps: App[] = [
    {
      id: "64f27b30-0cfa-4a71-be86-cee9f60a8ee0",
      name: i18n.departments.accounting.apps.tagesinkasso.title,
      department: accounting,
      description: i18n.departments.accounting.apps.tagesinkasso.description,
      isVisible: checkHasAnyPermission([Permission.TIBuchRead]),
      onNavigate: () => openInNewTab("https://tagesinkassobuch.hgv.it/"),
    },
    {
      id: "338ac8af-e647-4d26-8a5e-6fa5b345392c",
      name: i18n.departments.accounting.apps.monatsdaten.title,
      department: accounting,
      description: i18n.departments.accounting.apps.monatsdaten.description,
      isVisible: checkHasAnyPermission([
        Permission.MonatsdatenDataWrite,
        Permission.MonatsdatenSettingsWrite,
      ]),
      onNavigate: () => openInNewTab("https://monatsdaten.hgv.it/"),
    },
    {
      id: "19ce9fd3-c566-442d-90a8-6ef0a47d9159",
      name: i18n.departments.accounting.apps.bilanzOnline.title,
      department: accounting,
      description: i18n.departments.accounting.apps.bilanzOnline.description,
      isVisible: checkHasAnyPermission([Permission.AccountingsRead]),
      onNavigate: () =>
        navigate("/kennzahlen/buchhalterische-drei-jahres-analyse"),
    },
    {
      id: "feb073af-de03-42a5-9b30-881f093d3020",
      name: i18n.departments.accounting.apps.businessCheck.title,
      department: accounting,
      description: i18n.departments.accounting.apps.businessCheck.description,
      isVisible: checkHasAnyPermission([Permission.BusinessCheckExecute]),
      onNavigate: () => openInNewTab("https://quick-analysis.hgv.it/"),
    },
    {
      id: "31e405f6-7241-4be0-b4e3-a5059e4fe304",
      name: i18n.departments.accounting.apps.openItems.title,
      department: accounting,
      description: i18n.departments.accounting.apps.openItems.description,
      isVisible: checkHasAnyPermission([Permission.OpenItemsRead]),
      onNavigate: () => navigate("/steuerberatung/offene-posten"),
    },
    {
      id: "9c154b27-848a-4ce6-a251-afba73920f0a",
      name: i18n.departments.accounting.apps.invoicePortal.title,
      department: accounting,
      description: i18n.departments.accounting.apps.invoicePortal.description,
      isVisible: checkHasAnyPermission([Permission.InvoicePortalExecute]),
      onNavigate: () => {
        if (!member) return;
        return getMetropolisUrl(member.id)
          .then((redirectUrl) => openInNewTab(redirectUrl))
          .catch(handleError);
      },
    },
    {
      id: "c8c6c480-443a-401a-bce2-c8f290de39c8",
      name: i18n.departments.accounting.apps.endbestaende.title,
      department: accounting,
      description: i18n.departments.accounting.apps.endbestaende.description,
      isVisible: checkHasAnyPermission([
        Permission.EndbestaendeWorksheetsWrite,
        Permission.EndbestaendeSettingsWrite,
      ]),
      publishedAt: new Date(2023, 11, 27),
      onNavigate: () => openInNewTab("https://endbestaende.hgv.it/"),
    },
    {
      id: "7380e1bb-ddb9-4cf0-aa1a-9deda4c0c850",
      name: i18n.departments.payroll.apps.arbeitssicherheit.title,
      department: payroll,
      description: i18n.departments.payroll.apps.arbeitssicherheit.description,
      isVisible: checkHasAnyPermission([
        Permission.DocumentsBetriebOnlineServiceRead,
        Permission.DocumentsBetriebStandardRead,
        Permission.DocumentsArbeitsvertraegeRead,
      ]),
      onNavigate: () => navigate("/personalberatung/arbeitssicherheit"),
    },
    {
      id: "bd8c9060-43d6-45e3-bf05-9d9665a24907",
      name: i18n.departments.payroll.apps.hourRegistration.title,
      department: payroll,
      description: i18n.departments.payroll.apps.hourRegistration.description,
      isVisible: checkHasAnyPermission([Permission.HourRegistrationExecute]),
      onNavigate: () => {
        if (!member) return;
        return getWebdeskUrl("stundenregister", member.id)
          .then((redirectUrl) => openInNewTab(redirectUrl))
          .catch(handleError);
      },
    },
    {
      id: "7df24aeb-28ce-465e-a9c1-fba7d68b1ed9",
      name: i18n.departments.payroll.apps.employeeRegistration.title,
      department: payroll,
      description:
        i18n.departments.payroll.apps.employeeRegistration.description,
      isVisible: checkHasAnyPermission([
        Permission.EmployeeRegistrationExecute,
      ]),
      onNavigate: () => {
        if (!member) return;
        return getWebdeskUrl("mitarbeitermeldung", member.id)
          .then((redirectUrl) => openInNewTab(redirectUrl))
          .catch(handleError);
      },
    },
    {
      id: "35c662b3-0583-4b09-91f9-7133c0c4f2ae",
      name: i18n.departments.payroll.apps.urgentEmployeeRegistration.title,
      department: payroll,
      description:
        i18n.departments.payroll.apps.urgentEmployeeRegistration.description,
      isVisible: checkHasAnyPermission([
        Permission.EmployeeRegistrationExecute,
      ]),
      onNavigate: () => navigate("/personalberatung/uniurg"),
    },
    {
      id: "e27893a8-6e38-4c7d-ab70-a113931e9812",
      name: i18n.departments.payroll.apps.emergencyService.title,
      department: payroll,
      description: i18n.departments.payroll.apps.emergencyService.description,
      isVisible: member?.giottoId !== undefined,
      onNavigate: () => navigate("/personalberatung/notfalldienst"),
    },
    {
      id: "b8939ffa-da27-4ef2-881e-e37eb2fa3cbf",
      name: i18n.departments.payroll.apps.arbeitAufAbruf.title,
      department: payroll,
      description: i18n.departments.payroll.apps.arbeitAufAbruf.description,
      isVisible: member?.giottoId !== undefined,
      publishedAt: new Date(2024, 8, 25),
      onNavigate: () => navigate("/personalberatung/arbeit-auf-abruf"),
    },
    {
      id: "93c8acea-10d9-4ef6-8ba9-0a9bb39164b0",
      name: i18n.departments.webMarketing.apps.dac7.title,
      department: webMarketing,
      description: i18n.departments.webMarketing.apps.dac7.description,
      publishedAt: new Date(2023, 12, 7),
      isVisible: checkHasAnyPermission([Permission.Dac7Write]),
      onNavigate: () => navigate("/web-marketing/dac7"),
    },
    {
      id: "43599ea8-5d1a-48cd-ac44-7d25a90511f9",
      name: i18n.departments.joinHospitality.apps.readinessCheck.title,
      department: joinHospitality,
      description:
        i18n.departments.joinHospitality.apps.readinessCheck.description,
      isVisible: true,
      onNavigate: () =>
        openInNewTab("https://easy-feedback.de/umfrage/1985304/N31LL8"),
    },
    {
      id: "7ca3ed27-3a85-4d18-922e-65fadbb736ec",
      name: i18n.departments.joinHospitality.apps.joinToolbox.title,
      department: joinHospitality,
      description:
        i18n.departments.joinHospitality.apps.joinToolbox.description,
      isVisible: checkHasAnyPermission([Permission.JoinHospitalityLevel1Read]),
      onNavigate: () =>
        openInNewTab("https://www.joinhospitality.info/join-community-toolbox"),
    },
    {
      id: "e92cf0c3-b041-4ed8-8ed9-7d83472ab69b",
      name: i18n.departments.joinHospitality.apps.joinJobOffers.title,
      department: joinHospitality,
      description:
        i18n.departments.joinHospitality.apps.joinJobOffers.description,
      isVisible: checkHasAnyPermission([Permission.JoinHospitalityLevel2Read]),
      onNavigate: () => navigate("/join-hospitality/join-job-offers"),
    },
    {
      id: "1b40804e-f73b-4da1-8fbe-a409a5f5587b",
      name: i18n.departments.joinHospitality.apps.continuingEducation.title,
      department: joinHospitality,
      description:
        i18n.departments.joinHospitality.apps.continuingEducation.description,
      isVisible: true,
      onNavigate: () =>
        openInNewTab(
          i18n.departments.joinHospitality.apps.continuingEducation.url,
        ),
    },
    {
      id: "9f17fe6e-3385-4f7a-8b17-b74464144284",
      name: i18n.departments.joinHospitality.apps.consultingAndCoaching.title,
      department: joinHospitality,
      description:
        i18n.departments.joinHospitality.apps.consultingAndCoaching.description,
      isVisible: checkHasAnyPermission([Permission.JoinHospitalityLevel1Read]),
      onNavigate: () =>
        openInNewTab(
          "https://www.hgv.it/de/dienstleistungen/unternehmensberatung",
        ),
    },
  ].map((app) => ({
    ...app,
    onNavigate: async () => {
      const result = app.onNavigate();
      if (result instanceof Promise) {
        await result;
      }
      addRecentlyUsedApp(app);
    },
  }));

  const getSortedRecentlyUsedApps = () => {
    const recentlyUsedAppIds = getRecentlyUsedAppIds();
    return (
      apps
        .filter(
          ({ id, isVisible }) => recentlyUsedAppIds.includes(id) && isVisible,
        )
        .sort(
          (a, b) =>
            recentlyUsedAppIds.indexOf(a.id) - recentlyUsedAppIds.indexOf(b.id),
        ) ?? []
    );
  };

  const value = {
    apps,
    recentlyUsedApps: getSortedRecentlyUsedApps().slice(0, 5),
    isValidating: status === "validating",
  };

  return <AppsContext.Provider value={value}>{children}</AppsContext.Provider>;
};

export const useApps = () => {
  return useContext(AppsContext);
};
